import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCw, ChevronDown, ChevronRight, Building2, Users, FileText, Calendar, ExternalLink, X, SlidersHorizontal, AlertTriangle, ArrowRightLeft } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import { getBodaccHistory, getBodaccAnnouncementTypes, BoddacHistoryEstablishment, BoddacAnnouncement } from "../../../interfaces/establishments";

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmt(d?: string | null): string {
    if (!d) return "—";
    try { return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(new Date(d)); }
    catch { return d; }
}

function typeBadgeClass(type?: string): string {
    const t = (type || "").toLowerCase();
    if (t.includes("immatricul") || t.includes("créat"))                         return "bg-green-100 text-green-800";
    if (t.includes("vente") || t.includes("cession") || t.includes("transfert")) return "bg-orange-100 text-orange-800";
    if (t.includes("modif"))                                                      return "bg-blue-100 text-blue-800";
    if (t.includes("liquid") || t.includes("dissol") || t.includes("radiat"))    return "bg-red-100 text-red-800";
    if (t.includes("redress") || t.includes("sauveg") || t.includes("procéd"))   return "bg-yellow-100 text-yellow-800";
    return "bg-gray-100 text-gray-600";
}

// ─── Single announcement row ─────────────────────────────────────────────────────

function AnnouncementRow({ a, index }: { a: BoddacAnnouncement; index: number }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const hasDetail = !!(a.company || (a.founders && a.founders.length > 0) || a.legalActs || a.buyer || a.previousOwner);

    const buyerLink = a.buyer?.establishmentId
        ? () => navigate(`/establishments/${a.buyer!.establishmentId}`)
        : null;

    return (
        <div className={index > 0 ? "border-t border-gray-100" : ""}>
            <div
                className={`flex items-start gap-3 px-4 py-2 ${hasDetail ? "cursor-pointer hover:bg-gray-50" : ""}`}
                onClick={() => hasDetail && setOpen(v => !v)}
            >
                <span className="text-xs text-gray-400 w-24 shrink-0 pt-0.5">{fmt(a.date)}</span>

                {/* Radiation badge replaces the type badge */}
                {a.isRadiation ? (
                    <span className="text-xs px-1.5 py-0.5 rounded-sm font-medium shrink-0 bg-red-100 text-red-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Radiation
                    </span>
                ) : (
                    <span className={`text-xs px-1.5 py-0.5 rounded-sm font-medium shrink-0 ${typeBadgeClass(a.announcementType)}`}>
                        {a.announcementType || "Annonce"}
                    </span>
                )}

                <span className="text-xs text-gray-700 flex-1 min-w-0">
                    <span className="truncate block">
                        {a.modificationDescription || a.announcementFamily || "—"}
                        {a.tribunal ? <span className="text-gray-400 ml-2">· {a.tribunal}</span> : null}
                    </span>
                </span>

                {/* Buyer chip (vente) */}
                {a.buyer?.siren && (
                    <button
                        onClick={e => { e.stopPropagation(); if (buyerLink) buyerLink(); }}
                        title={`Acheteur : ${a.buyer.denomination || a.buyer.siren}`}
                        className={`hidden md:flex items-center gap-1 text-xs shrink-0 px-1.5 py-0.5 rounded-sm border transition-colors ${
                            buyerLink
                                ? "border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                                : "border-gray-200 text-gray-500 bg-gray-50 cursor-default"
                        }`}
                    >
                        <ArrowRightLeft className="w-3 h-3" />
                        {a.buyer.denomination || a.buyer.siren}
                        {buyerLink && <ExternalLink className="w-2.5 h-2.5 ml-0.5" />}
                    </button>
                )}

                {a.founders && a.founders.length > 0 && !a.buyer && (
                    <span className="text-xs text-gray-400 hidden md:flex items-center gap-1 shrink-0">
                        <Users className="w-3 h-3" />
                        {a.founders.map(f => f.fullName || [f.prenom, f.nom].filter(Boolean).join(" ")).join(", ")}
                    </span>
                )}

                {hasDetail && (open
                    ? <ChevronDown className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5" />
                    : <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5" />
                )}
            </div>

            {open && hasDetail && (
                <div className="px-4 pb-3 pt-1 bg-gray-50 border-t border-gray-100 text-xs space-y-2">

                    {/* Radiation notice */}
                    {a.isRadiation && (
                        <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            <span>Cet établissement a été radié du RCS — marqué comme fermé dans l'application.</span>
                        </div>
                    )}

                    {/* Vente: buyer ↔ seller */}
                    {(a.buyer || a.previousOwner) && (
                        <div>
                            <p className="font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <ArrowRightLeft className="w-3 h-3" /> Cession / Rachat
                            </p>
                            <div className="flex items-center gap-4 flex-wrap text-gray-600">
                                {a.previousOwner && (
                                    <span>
                                        <b>Cédant :</b> {a.previousOwner.denomination || "—"}
                                        {a.previousOwner.siren && <span className="text-gray-400 ml-1">(SIREN {a.previousOwner.siren})</span>}
                                    </span>
                                )}
                                {a.buyer && (
                                    <span className="flex items-center gap-1">
                                        <b>Acheteur :</b> {a.buyer.denomination || "—"}
                                        {a.buyer.siren && <span className="text-gray-400 ml-1">(SIREN {a.buyer.siren})</span>}
                                        {a.buyer.establishmentId && (
                                            <button
                                                onClick={() => navigate(`/establishments/${a.buyer!.establishmentId}`)}
                                                className="ml-1 flex items-center gap-0.5 text-blue-600 hover:text-blue-800"
                                            >
                                                <ExternalLink className="w-3 h-3" /> Voir la fiche
                                            </button>
                                        )}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {a.company && (
                        <div>
                            <p className="font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> Société
                            </p>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-gray-600">
                                {a.company.denomination    && <span><b>Dénomination :</b> {a.company.denomination}</span>}
                                {a.company.commercialName  && <span><b>Nom commercial :</b> {a.company.commercialName}</span>}
                                {a.company.legalForm       && <span><b>Forme juridique :</b> {a.company.legalForm}</span>}
                                {a.company.capital         && <span><b>Capital :</b> {a.company.capital.amount} {a.company.capital.currency}</span>}
                                {a.company.address?.city   && (
                                    <span>
                                        <b>Adresse :</b>{" "}
                                        {[a.company.address.streetNumber, a.company.address.streetType, a.company.address.streetName].filter(Boolean).join(" ")}
                                        {a.company.address.postalCode ? `, ${a.company.address.postalCode}` : ""} {a.company.address.city}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {a.founders && a.founders.length > 0 && (
                        <div>
                            <p className="font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <Users className="w-3 h-3" /> Dirigeants
                            </p>
                            {a.founders.map((f, i) => (
                                <span key={i} className="block text-gray-600">
                                    <b>{f.fullName || [f.prenom, f.nom].filter(Boolean).join(" ") || "—"}</b>
                                    {f.qualite       && <span className="text-gray-400"> · {f.qualite}</span>}
                                    {f.dateNaissance && <span className="text-gray-400"> · né·e le {f.dateNaissance}</span>}
                                    {f.nationalite   && <span className="text-gray-400"> · {f.nationalite}</span>}
                                </span>
                            ))}
                        </div>
                    )}

                    {a.legalActs && Object.values(a.legalActs).some(Boolean) && (
                        <div>
                            <p className="font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Actes
                            </p>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-gray-600">
                                {a.legalActs.creationDate      && <span><b>Création :</b> {a.legalActs.creationDate}</span>}
                                {a.legalActs.registrationDate  && <span><b>Immatriculation :</b> {a.legalActs.registrationDate}</span>}
                                {a.legalActs.activityStartDate && <span><b>Début d'activité :</b> {a.legalActs.activityStartDate}</span>}
                                {a.legalActs.creationCategory  && <span><b>Catégorie :</b> {a.legalActs.creationCategory}</span>}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Establishment card ──────────────────────────────────────────────────────────

function EstablishmentCard({ e }: { e: BoddacHistoryEstablishment }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    return (
        <div className="border border-gray-200 rounded-sm mb-2 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                {/* Expand toggle */}
                <button
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    onClick={() => setOpen(v => !v)}
                >
                    {open
                        ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}

                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{e.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            SIREN : {e.siren || "—"}
                            {e.city ? ` · ${e.city}` : ""}
                        </p>
                    </div>
                </button>

                {/* Metadata + actions */}
                <div className="flex items-center gap-2 shrink-0 text-xs">
                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-sm font-medium">
                        {e.bodaccCount} annonce{e.bodaccCount !== 1 ? "s" : ""}
                    </span>

                    {e.latestDate && (
                        <span className="text-gray-400 hidden sm:flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {fmt(e.latestDate)}
                        </span>
                    )}

                    {/* Open establishment page */}
                    <button
                        title="Ouvrir la fiche établissement"
                        onClick={() => navigate(`/establishments/${e._id}`)}
                        className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-sm hover:bg-white hover:border-gray-400 text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Fiche</span>
                    </button>
                </div>
            </div>

            {open && (
                <div className="border-t border-gray-100">
                    <div className="flex items-center gap-3 px-4 py-1.5 bg-gray-50 text-xs text-gray-400 font-medium border-b border-gray-100">
                        <span className="w-24 shrink-0">Date</span>
                        <span className="w-32 shrink-0">Type</span>
                        <span className="flex-1">Titre / Famille</span>
                        <span className="hidden md:block">Dirigeants</span>
                    </div>
                    {e.announcements.map((a, i) => (
                        <AnnouncementRow key={a._id || i} a={a} index={i} />
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Filter panel ────────────────────────────────────────────────────────────────

interface Filters {
    name: string;
    siren: string;
    announcementType: string;
    minCount: string;
    maxCount: string;
}

const EMPTY_FILTERS: Filters = { name: "", siren: "", announcementType: "", minCount: "", maxCount: "" };

function FilterPanel({
    filters,
    onChange,
    onApply,
    onReset,
    announcementTypes,
}: {
    filters: Filters;
    onChange: (f: Filters) => void;
    onApply: () => void;
    onReset: () => void;
    announcementTypes: string[];
}) {
    const set = (key: keyof Filters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        onChange({ ...filters, [key]: e.target.value });

    const hasActive = Object.values(filters).some(v => v !== "");

    return (
        <form
            className="mb-5 p-4 border border-gray-200 rounded-sm bg-gray-50 space-y-3"
            onSubmit={e => { e.preventDefault(); onApply(); }}
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nom d'établissement</label>
                    <input
                        type="text"
                        value={filters.name}
                        onChange={set("name")}
                        placeholder="ex. : Le Bistrot"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-black bg-white"
                    />
                </div>

                {/* SIREN */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">SIREN</label>
                    <input
                        type="text"
                        value={filters.siren}
                        onChange={set("siren")}
                        placeholder="ex. : 812345678"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-black bg-white"
                    />
                </div>

                {/* Announcement type */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type d'annonce</label>
                    <div className="relative">
                        <select
                            value={filters.announcementType}
                            onChange={set("announcementType")}
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-black bg-white appearance-none"
                        >
                            <option value="">Tous les types</option>
                            {announcementTypes.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    </div>
                </div>

                {/* Min count */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nb d'annonces (min)</label>
                    <input
                        type="number"
                        min={0}
                        value={filters.minCount}
                        onChange={set("minCount")}
                        placeholder="ex. : 2"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-black bg-white"
                    />
                </div>

                {/* Max count */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nb d'annonces (max)</label>
                    <input
                        type="number"
                        min={0}
                        value={filters.maxCount}
                        onChange={set("maxCount")}
                        placeholder="ex. : 10"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-black bg-white"
                    />
                </div>
            </div>

            <div className="flex gap-2 pt-1">
                <button
                    type="submit"
                    className="px-4 py-1.5 text-sm bg-black text-white rounded-sm hover:bg-gray-800"
                >
                    Appliquer
                </button>
                {hasActive && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-sm hover:bg-white text-gray-500"
                    >
                        <X className="w-3.5 h-3.5" /> Réinitialiser
                    </button>
                )}
            </div>
        </form>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────────

export default function BoddacHistory() {
    const [data, setData]         = useState<{ establishments: BoddacHistoryEstablishment[]; total: number; totalPages: number } | null>(null);
    const [page, setPage]         = useState(1);
    const [filters, setFilters]   = useState<Filters>(EMPTY_FILTERS);
    const [applied, setApplied]   = useState<Filters>(EMPTY_FILTERS);   // committed on Apply
    const [showFilters, setShowFilters] = useState(true);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState<string | null>(null);
    const [announcementTypes, setAnnouncementTypes] = useState<string[]>([]);

    // Fetch distinct announcement types once on mount
    useEffect(() => {
        getBodaccAnnouncementTypes()
            .then(setAnnouncementTypes)
            .catch(() => setAnnouncementTypes([]));
    }, []);

    const LIMIT = 20;

    const load = useCallback(async (p: number, f: Filters) => {
        setLoading(true);
        setError(null);
        try {
            const res = await getBodaccHistory({
                page:             p,
                limit:            LIMIT,
                name:             f.name     || undefined,
                siren:            f.siren    || undefined,
                announcementType: f.announcementType || undefined,
                minCount:         f.minCount ? parseInt(f.minCount, 10) : undefined,
                maxCount:         f.maxCount ? parseInt(f.maxCount, 10) : undefined,
            });
            setData(res);
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(page, applied); }, [load, page, applied]);

    const handleApply = () => {
        setPage(1);
        setApplied(filters);
    };

    const handleReset = () => {
        const empty = EMPTY_FILTERS;
        setFilters(empty);
        setApplied(empty);
        setPage(1);
    };

    const activeFilterCount = Object.values(applied).filter(v => v !== "").length;

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <BONavigation />

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Historique BODACC</h1>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {data != null
                            ? `${data.total.toLocaleString("fr-FR")} établissement${data.total !== 1 ? "s" : ""} avec des annonces BODACC`
                            : "Chargement…"}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowFilters(v => !v)}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-sm transition-colors ${
                            showFilters ? "border-black bg-black text-white" : "border-gray-200 hover:bg-gray-50 text-gray-600"
                        }`}
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        Filtres
                        {activeFilterCount > 0 && (
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${showFilters ? "bg-white text-black" : "bg-black text-white"}`}>
                                {activeFilterCount}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => load(page, applied)}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Actualiser
                    </button>
                </div>
            </div>

            {/* Filter panel */}
            {showFilters && (
                <FilterPanel
                    filters={filters}
                    onChange={setFilters}
                    onApply={handleApply}
                    onReset={handleReset}
                    announcementTypes={announcementTypes}
                />
            )}

            {/* Quick-search bar (visible when filter panel is hidden) */}
            {!showFilters && (
                <form
                    className="flex gap-2 mb-5"
                    onSubmit={e => { e.preventDefault(); handleApply(); }}
                >
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={filters.name}
                            onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
                            placeholder="Rechercher par nom ou SIREN…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-black"
                        />
                    </div>
                    <button type="submit" className="px-4 py-2 text-sm bg-black text-white rounded-sm hover:bg-gray-800">
                        Chercher
                    </button>
                </form>
            )}

            {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            {loading && !data ? (
                <div className="text-sm text-gray-400 py-12 text-center">Chargement…</div>
            ) : !data || data.establishments.length === 0 ? (
                <div className="text-sm text-gray-400 py-12 text-center">
                    {activeFilterCount > 0
                        ? "Aucun résultat pour ces filtres."
                        : "Aucun établissement avec des annonces BODACC pour l'instant — le cron est en cours de traitement."}
                </div>
            ) : (
                <>
                    {data.establishments.map(e => <EstablishmentCard key={e._id} e={e} />)}

                    {data.totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                            <span>Page {page} / {data.totalPages}</span>
                            <div className="flex gap-2">
                                <button
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage(p => p - 1)}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-40"
                                >
                                    ← Précédent
                                </button>
                                <button
                                    disabled={page >= data.totalPages || loading}
                                    onClick={() => setPage(p => p + 1)}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-40"
                                >
                                    Suivant →
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
